import axios from "axios";

// Same report flow as the reference game (mythfact): one function that ADDS a report when there is
// no reportId yet and UPDATES it when there is. URLs are built from REACT_APP_REPORTS_BASE_URL.

const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;
const baseUrl = () => (process.env.REACT_APP_REPORTS_BASE_URL || "").replace(/\/+$/, "");

const uuid4 = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

/** 'GUEST_USER' | 'ORG_USER' | 'demo' from the stored user (login stores it as `source`). */
export function resolveReportRole(user) {
  const raw = user?.source || user?.role || user?.roles || user?.user_type || "";
  if (/demo/i.test(raw)) return "demo";
  return raw === "GUEST_USER" ? "GUEST_USER" : "ORG_USER";
}

/** Pull the report id out of a game-server response (shape varies by endpoint). */
export function extractReportIdFromResponse(payload) {
  if (payload == null || typeof payload !== "object") return null;
  const candidates = [
    payload.id,
    payload.reportId,
    payload.report_id,
    payload.data?.id,
    payload.data?.reportId,
    payload.data?.report_id,
    payload.result?.id,
    payload.result?.reportId,
    payload.report?.id,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== "") return String(c).trim();
  }
  return null;
}

export async function refreshToken(currentToken) {
  const headers = {
    "client-secret": CLIENT_SECRET,
    "Content-Type": "application/json",
    ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
    "Idempotency-Key": uuid4(),
  };
  const response = await axios.get(`${baseUrl()}/auth/rf-token`, { headers });
  return response.data.token;
}

/**
 * @param {object} data { role, token, reportId, points, time, gameId, name, sessionId, userId, organizationId }
 * @returns {Promise<object|true>} response body (or true when skipped for demo)
 */
export async function sendReport(data) {
  const { role, token, reportId } = data;
  if (role === "demo" || role === "demobypass") return true;

  const base = baseUrl();

  if (role === "GUEST_USER") {
    const isUpdate = Boolean(reportId);
    const url = `${base}/${isUpdate ? "game/update/guest/score" : "game/add/guest/score"}`;
    const body = isUpdate
      ? { points: data.points, time: data.time, reportId }
      : {
          points: data.points,
          time: data.time,
          gameId: data.gameId,
          name: data.name,
          sessionId: data.sessionId,
          userId: data.userId,
          organizationId: data.organizationId,
          ans: "",
        };
    const headers = {
      "client-secret": CLIENT_SECRET,
      "Content-Type": "application/json",
      "Idempotency-Key": uuid4(),
    };
    const response = await axios({ method: isUpdate ? "put" : "post", url, headers, data: body });
    return response.data;
  }

  // Registered (org) user
  const isUpdate = Boolean(reportId);
  const url = isUpdate ? `${base}/game-server/report/${reportId}` : `${base}/game-server/report/add`;
  const method = isUpdate ? "put" : "post";
  const body = { points: data.points, time: data.time, ...(isUpdate && { reportId }) };
  const headers = {
    "client-secret": CLIENT_SECRET,
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Idempotency-Key": uuid4(),
  };

  try {
    const response = await axios({ method, url, headers, data: body });
    return response.data;
  } catch (err) {
    if (err.response?.status === 401) {
      const newToken = await refreshToken(token);
      headers.Authorization = `Bearer ${newToken}`;
      const retry = await axios({ method, url, headers, data: body });
      return retry.data;
    }
    throw err;
  }
}
